/* eslint-disable @next/next/no-img-element */
"use client";
import { useMapContext } from "@components/sections/mapSection/MapProvider";
import { FacilityClusterPoint } from "@lib/utils/types/FacilityClusterPoint";
import { getBounds } from "@repo/map-helpers";
import {
  FacilityDetailsResponse,
  FacilitySummaryResponse,
  PathResponse,
} from "@repo/shared-types";
import { Suspense, useCallback, useEffect, useRef, useState } from "react";
import MapGL, {
  GeolocateControl,
  Layer,
  MapLayerMouseEvent,
  MapRef,
  NavigationControl,
  ScaleControl,
  Source,
} from "react-map-gl";

import { Clusters } from "./cluster/Cluster";
import {
  BIKING_LINE_PAINT_FALLBACK,
  HOVER_POINT_PAINT,
  INTERACTIVE_AREA_LINE_PAINT,
  LINE_LAYOUT,
  MAP_CENTER,
  MAP_SCALE_STYLE,
  MAP_STYLE,
  MAX_CLUSTER_ZOOM,
  OUTLINE_PAINT,
  START_END_POINT_PAINT,
  WALKING_LINE_PAINT_FALLBACK,
  ZOOM_ANIMATION_OPTIONS,
} from "./consts";
import { DistanceToPointPopup } from "./distanceToPointPopup/DistanceToPointPopup";
import { useClusters, useDistanceCalculation, usePathPoints } from "./hooks";
import { IMarkerPopup, MarkerPopup } from "./markerPopup/MarkerPopup";
import PathPopup from "./pathPopup/PathPopup";
import { POI } from "./poi/POI";
import { getPathIdFromLayer } from "./utils";

interface IMap {
  paths: PathResponse[];
  facilities: FacilitySummaryResponse[];
  poi: FacilityDetailsResponse | null;
}

const lines = {
  biking: BIKING_LINE_PAINT_FALLBACK,
  hiking: WALKING_LINE_PAINT_FALLBACK,
};

export default function Map({ paths, facilities, poi }: IMap) {
  const mapRef = useRef<MapRef>(null);
  const { markerPopup, recentlyChosenFilter, setMarkerPopup } = useMapContext();
  const [selectedPath, setSelectedPath] = useState<PathResponse | undefined>(
    undefined
  );

  const resetZoom = useCallback(() => {
    if (!paths.length || recentlyChosenFilter?.name === "categoryId") {
      return;
    }
    const { minLat, minLng, maxLat, maxLng } = getBounds(paths);
    mapRef.current?.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      ZOOM_ANIMATION_OPTIONS
    );
  }, [paths, recentlyChosenFilter]);

  useEffect(() => {
    resetZoom();
  }, [resetZoom, recentlyChosenFilter]);

  const [clusters, supercluster, _zoom, setZoom] = useClusters(
    facilities,
    mapRef
  );

  function onClusterClickEventHandler(clusterPoint: FacilityClusterPoint) {
    const expansionZoom = supercluster
      ? Math.min(
          supercluster.getClusterExpansionZoom(Number(clusterPoint.id)),
          MAX_CLUSTER_ZOOM
        )
      : MAX_CLUSTER_ZOOM;

    mapRef.current?.setCenter({
      lat: clusterPoint.latitude,
      lon: clusterPoint.longitude,
    });
    mapRef.current?.zoomTo(expansionZoom);
    setZoom(expansionZoom);
  }

  function onMarkerClick(marker: Omit<IMarkerPopup, "onClose">) {
    if (!mapRef) return;
    mapRef.current?.setCenter(
      { lon: marker.longitude, lat: marker.latitude },
      ZOOM_ANIMATION_OPTIONS
    );
    setMarkerPopup(marker);
  }

  function onClick(event: MapLayerMouseEvent) {
    if (!mapRef) return;
    if (event.originalEvent.target !== event.target.getCanvas()) return;
    if (!event.features) return;
    try {
      const pathId = getPathIdFromLayer(event.features[0].layer);
      const path = paths.find(({ id }) => id === pathId);
      setMarkerPopup(null);
      setSelectedPath(path);
      if (path) {
        const { minLat, minLng, maxLat, maxLng } = getBounds([path]);
        mapRef.current?.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          ZOOM_ANIMATION_OPTIONS
        );
      }
    } catch {
      setSelectedPath(undefined);
    }
  }

  const pathPoints = usePathPoints(selectedPath);
  const { distances, pointPosition, pointSource, calculateDistances } =
    useDistanceCalculation();

  const onMouseMove = (event: MapLayerMouseEvent) => {
    if (!mapRef.current || !selectedPath) return;

    const { lngLat, features } = event;
    const hoveredLayer = features?.find(
      feature => getPathIdFromLayer(feature.layer) === selectedPath.id
    )?.layer;
    const layerId = hoveredLayer && getPathIdFromLayer(hoveredLayer);

    mapRef.current.getCanvas().style.cursor =
      layerId && selectedPath.id === layerId ? "pointer" : "";

    calculateDistances(lngLat, selectedPath, layerId);
  };

  return (
    <MapGL
      mapboxAccessToken={
        process.env.NEXT_PUBLIC_MAXBOX_TOKEN ||
        "pk.eyJ1IjoiLWthdHJpbmU4NiIsImEiOiJjbHhuM2o2Z3QwZW9tMnJzZXdyZ3d6bjVkIn0.myE9RFFZikeU-1cB0Hb69w"
      }
      initialViewState={{ ...MAP_CENTER }}
      mapStyle={MAP_STYLE}
      style={{
        height: "100vh",
        position: "relative",
      }}
      scrollZoom={true}
      styleDiffing={true}
      onZoom={e => {
        setZoom(e.viewState.zoom);
      }}
      ref={mapRef}
      interactiveLayerIds={paths.map(path => `interactiveArea.${path.id}`)}
      onClick={onClick}
      onMouseMove={onMouseMove}
    >
      <GeolocateControl
        style={{
          margin: "48px 10px 0 0",
          borderRadius: "8px 8px 0 0",
        }}
      />
      <NavigationControl
        style={{
          margin: "0 10px 0 0",
          borderRadius: "0 0 8px 8px",
        }}
        showCompass={false}
      />
      {poi && <POI poi={poi} onMarkerClick={onMarkerClick} />}
      <Clusters
        clusters={clusters}
        onClusterClickEventHandler={onClusterClickEventHandler}
        onMarkerClick={onMarkerClick}
      />
      {pathPoints && (
        <Source type="geojson" id="startEndSource" data={pathPoints}>
          <Layer type="circle" id="pathPoints" paint={START_END_POINT_PAINT} />
        </Source>
      )}
      {paths.map((path: PathResponse) => (
        <Source
          type="geojson"
          id={`source.${path.id}`}
          key={path.id}
          data={path.geoJson}
        >
          <Layer
            id={`interactiveArea.${path.id}`}
            type="line"
            layout={LINE_LAYOUT}
            paint={INTERACTIVE_AREA_LINE_PAINT}
          />
          <Layer
            id={`overlay.${path.id}`}
            type="line"
            layout={{ visibility: "none" }}
          />
          <Layer
            beforeId={
              selectedPath
                ? selectedPath.id !== path.id
                  ? selectedPath.id
                  : pathPoints
                    ? "pathPoints"
                    : undefined
                : `overlay.${path.id}`
            }
            id={path.id}
            type="line"
            layout={LINE_LAYOUT}
            paint={
              selectedPath && path.id !== selectedPath.id
                ? { ...lines[path.type], "line-opacity": 0.5 }
                : { ...lines[path.type] }
            }
          />
          <Layer
            beforeId={path.id}
            id={`outline.${path.id}`}
            type="line"
            layout={LINE_LAYOUT}
            paint={
              path.id !== selectedPath?.id
                ? { ...OUTLINE_PAINT, "line-opacity": 0 }
                : { ...OUTLINE_PAINT }
            }
          />
        </Source>
      ))}

      {pointSource && (
        <Source type="geojson" id="pointSource" data={pointSource}>
          <Layer id="hoverPoint" type="circle" paint={HOVER_POINT_PAINT} />
        </Source>
      )}

      {pointPosition && selectedPath && distances && (
        <DistanceToPointPopup
          longitude={pointPosition[0]}
          latitude={pointPosition[1]}
          path={selectedPath}
          distances={distances}
        />
      )}
      {selectedPath && (
        <PathPopup
          selectedPath={selectedPath}
          clear={() => setSelectedPath(undefined)}
        />
      )}
      {markerPopup && (
        <Suspense>
          <MarkerPopup {...markerPopup} onClose={() => setMarkerPopup(null)} />
        </Suspense>
      )}
      <ScaleControl position="top-left" style={MAP_SCALE_STYLE} />
    </MapGL>
  );
}
